import { SortDirectionEnum } from '@gripp/shared-logic';
import { ReactNode } from 'react';

/**
 * An opt-in, display-only row interleaved into {@link InfiniteDataGrid} at its
 * `sortValue`, merged against the grid's active sort field + direction. Used to
 * place per-run GeoTab sync cards chronologically in the activity feed (Plan
 * 0019) without turning them into real `Issue`/`Activity` rows — they never
 * consume an activity slot, shift the query offset, or participate in selection.
 */
export interface InterleavedRow {
  /** Stable React key + de-dupe id. */
  id: string;
  /** Where this row sorts, compared against the active sort field's value. */
  sortValue: Date;
  /** Renders the row body (e.g. a per-run card). */
  render: () => ReactNode;
}

export type MergedRow<TDataRow> =
  | { kind: 'data'; key: string; row: TDataRow }
  | { kind: 'interleaved'; key: string; row: InterleavedRow };

/** Coerce an ISO string / Date / epoch number to epoch ms, or null if absent/unparseable. */
const toEpoch = (value: unknown): number | null => {
  if (value == null) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  const t = new Date(value as string | number).getTime();
  return Number.isNaN(t) ? null : t;
};

export interface MergeInterleavedOptions<TDataRow> {
  /** Server-sorted data rows, already in `sortOrder` (grid does not re-sort). */
  dataRows: TDataRow[];
  interleavedRows: InterleavedRow[] | undefined;
  /** Active sort field on each data row's underlying object (e.g. 'createdAt'). */
  sortField: string | undefined;
  /** Active sort direction; `null`/`undefined` are treated as descending. */
  sortOrder: SortDirectionEnum | null | undefined;
  /** True while more pages remain — gates the window guard (see below). */
  hasNextPage: boolean;
  /** Stable key for a data row (defaults to `row.id`). */
  dataRowKey: (row: TDataRow) => string;
  /** Sort value for a data row (defaults to reading `sortField` off the row). */
  dataRowSortValue?: (row: TDataRow) => number | null;
}

/**
 * Merge interleaved rows into the server-sorted data rows by sort value,
 * producing the display list.
 *
 * ## Window guard (the subtle part — see Plan 0019 Risks)
 * An interleaved row that sorts *beyond the last loaded data row* is held back
 * while more pages remain — i.e. older-than-oldest for a descending sort, or
 * newer-than-newest for an ascending sort. It appears only once pagination has
 * loaded that far; otherwise it would clamp to the bottom of a partially-loaded
 * list and misrepresent its position. A row that sorts *within or ahead of* the
 * loaded window is placed immediately (for desc, a just-happened run pins to the
 * top even if the newest issue is older). When the list is fully loaded
 * (`!hasNextPage`), the guard lifts and all remaining rows are flushed at the
 * tail so nothing is silently dropped.
 *
 * Purely positional: does not mutate `dataRows`, so the grid's row model,
 * selection, offset, and activity count are untouched (empty `interleavedRows`
 * ⇒ byte-identical output to the data rows alone).
 */
export function mergeInterleavedRows<TDataRow>({
  dataRows,
  interleavedRows,
  sortField,
  sortOrder,
  hasNextPage,
  dataRowKey,
  dataRowSortValue,
}: MergeInterleavedOptions<TDataRow>): MergedRow<TDataRow>[] {
  const dataOnly = (): MergedRow<TDataRow>[] =>
    dataRows.map((row) => ({ kind: 'data', key: dataRowKey(row), row }));

  if (!interleavedRows || interleavedRows.length === 0) {
    return dataOnly();
  }

  const asc = sortOrder === SortDirectionEnum.Asc;
  const keyOfData = (row: TDataRow): number | null =>
    dataRowSortValue ? dataRowSortValue(row) : toEpoch((row as Record<string, unknown>)?.[sortField ?? '']);

  // Sort interleaved rows in display order (larger-first for desc, smaller-first for asc).
  const sortedInterleaved = [...interleavedRows].sort((a, b) => {
    const diff = a.sortValue.getTime() - b.sortValue.getTime();
    return asc ? diff : -diff;
  });

  // `beforeData(i, d)`: does interleaved row i come before data-row key d in display order?
  const beforeData = (iKey: number, dKey: number): boolean =>
    asc ? iKey < dKey : iKey > dKey;

  const out: MergedRow<TDataRow>[] = [];
  let ii = 0;
  for (const row of dataRows) {
    const dKey = keyOfData(row);
    // Flush interleaved rows that sort before this data row. A data row with no
    // sortable key can't anchor a comparison — emit it and let interleaved rows
    // wait for the next comparable data row (or the tail).
    if (dKey != null) {
      while (ii < sortedInterleaved.length && beforeData(sortedInterleaved[ii].sortValue.getTime(), dKey)) {
        const r = sortedInterleaved[ii++];
        out.push({ kind: 'interleaved', key: r.id, row: r });
      }
    }
    out.push({ kind: 'data', key: dataRowKey(row), row });
  }

  // Tail: remaining interleaved rows sort beyond the oldest loaded data row.
  // Only flush them when the list is fully loaded; otherwise hold them back so
  // they land in-window as more pages arrive (the window guard).
  if (!hasNextPage) {
    while (ii < sortedInterleaved.length) {
      const r = sortedInterleaved[ii++];
      out.push({ kind: 'interleaved', key: r.id, row: r });
    }
  }

  return out;
}
